import { Text, type TextStyleOptions } from "pixi.js";
import { LocalizedText } from "@slot/game-shell";
import type { LocalizationService } from "@slot/game-shell";

/** Roboto from the shell's fonts (loaded with `shell-ui` before the scene exists). */
export const FONT = "Roboto, system-ui, sans-serif";

/**
 * The panels' type, from the Cocos labels: white Roboto Medium with a thin black outline and a soft black shadow
 * (outline 1, shadow blur 10, no offset).
 */
export const TEXT = {
  panel: {
    fontFamily: FONT,
    fontWeight: "500",
    fill: 0xffffff,
    stroke: { color: 0x000000, width: 2, join: "round" },
    dropShadow: { color: 0x000000, alpha: 1, blur: 10, distance: 0 },
    align: "center",
  } satisfies TextStyleOptions,
  /**
   * The multipliers (a battle's two values, the Super Bonus saved total): white bold Roboto with a black outline and a
   * soft black shadow (the Cocos labels: Roboto Bold, or Roboto Medium set bold; outline 2, shadow blur 10, no offset).
   */
  multiplier: {
    fontFamily: FONT,
    fontWeight: "700",
    fill: 0xffffff,
    stroke: { color: 0x000000, width: 4, join: "round" },
    dropShadow: { color: 0x000000, alpha: 1, blur: 10, distance: 0 },
    align: "center",
  } satisfies TextStyleOptions,
} as const;

/** A localized line, centred at `y`, shrunk to `box` when a language runs long (the Cocos labels' SHRINK overflow). */
export function label(input: {
  localization: LocalizationService;
  key: string;
  fallback: string;
  style: TextStyleOptions;
  fontSize: number;
  y: number;
  box: { width: number; height: number };
  label: string;
}): LocalizedText {
  const text = new LocalizedText({
    localization: input.localization,
    key: input.key,
    fallback: input.fallback,
    style: { ...input.style, fontSize: input.fontSize },
    label: input.label,
    fitBox: input.box,
  });
  text.position.set(0, input.y);
  return text;
}

/** How a `FittedLine` is placed: its box centred at `y`, the text centred in it or from its top; its line spacing. */
interface FittedLineSpec {
  style: TextStyleOptions;
  fontSize: number;
  y: number;
  box: { width: number; height: number };
  wrap?: boolean;
  /** The Cocos label's vertical alignment: "top" starts the text at the box's top edge. Default "center". */
  verticalAlign?: "center" | "top";
  /** The Cocos label's line height (a wrapped text's line spacing); default, the font's own. */
  lineHeight?: number;
  label: string;
}

/** A line whose string the view sets (amounts, the per-feature texts), in its box centred at `y`, shrunk to fit. */
export function value(input: FittedLineSpec): FittedLine {
  return new FittedLine(input);
}

/**
 * A Pixi text the view sets, shrunk (never grown) to fit its box; with `wrap`, it wraps at the box's width first and
 * shrinks to its height. A top-aligned one shrinks toward the box's top edge.
 */
export class FittedLine extends Text {
  private readonly box: { width: number; height: number };

  public constructor(input: FittedLineSpec) {
    super({
      text: "",
      style: {
        ...input.style,
        fontSize: input.fontSize,
        ...(input.lineHeight !== undefined ? { lineHeight: input.lineHeight } : {}),
        ...(input.wrap ? { wordWrap: true, wordWrapWidth: input.box.width } : {}),
      },
      label: input.label,
    });
    this.box = input.box;
    const top = input.verticalAlign === "top";
    this.anchor.set(0.5, top ? 0 : 0.5);
    this.position.set(0, top ? input.y - input.box.height / 2 : input.y);
  }

  /** The string, then the fit. */
  public setLine(text: string): void {
    this.text = text;
    this.scale.set(1, 1);
    const fit = Math.min(1, this.box.width / Math.max(1, this.width), this.box.height / Math.max(1, this.height));
    this.scale.set(fit, fit);
  }
}
